import { useEffect, useRef, createElement } from "react";
import type { ReactElement } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useRoles } from "@/hooks/useRoles";
import { useActiveCoach } from "@/hooks/useActiveCoach";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";

const STORAGE_KEY = "smrtlyfit_attention_last_shown";
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

function wasShownToday(): boolean {
  const last = localStorage.getItem(STORAGE_KEY);
  if (!last) return false;
  return Date.now() - Number(last) < ONE_DAY_MS;
}

function markShown() {
  localStorage.setItem(STORAGE_KEY, String(Date.now()));
}

interface AlertItem {
  text: string;
  route: string;
}

/**
 * Shows once-per-day toast alerts for items needing user/admin/coach attention.
 * Each alert links to the relevant page.
 */
export function useAttentionAlerts() {
  const { user } = useAuth();
  const { isAdmin, isCoach } = useRoles();
  const { activeCoach } = useActiveCoach();
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    if (!user || wasShownToday()) return;

    const timeout = setTimeout(() => {
      checkAlerts();
    }, 2000); // slight delay so UI settles

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isAdmin, isCoach]);

  async function checkAlerts() {
    if (!user) return;
    const alerts: AlertItem[] = [];

    try {
      // --- Admin alerts ---
      if (isAdmin) {
        const { count: pendingCerts } = await supabase
          .from("coach_certifications")
          .select("id", { count: "exact", head: true })
          .eq("status", "pending");

        if (pendingCerts && pendingCerts > 0) {
          alerts.push({
            text: `${pendingCerts} certification${pendingCerts > 1 ? "s" : ""} pending review`,
            route: "/admin",
          });
        }

        const { count: unresolvedFlags } = await supabase
          .from("flags")
          .select("id", { count: "exact", head: true })
          .eq("resolved", false);

        if (unresolvedFlags && unresolvedFlags > 0) {
          alerts.push({
            text: `${unresolvedFlags} unresolved security flag${unresolvedFlags > 1 ? "s" : ""}`,
            route: "/admin",
          });
        }
      }

      // --- Coach alerts ---
      if (isCoach) {
        const { count: pendingRequests } = await supabase
          .from("coach_requests")
          .select("id", { count: "exact", head: true })
          .eq("coach_id", user.id)
          .eq("status", "pending");

        if (pendingRequests && pendingRequests > 0) {
          alerts.push({
            text: `${pendingRequests} pending coach request${pendingRequests > 1 ? "s" : ""}`,
            route: "/coach",
          });
        }

        const { count: pendingLeads } = await supabase
          .from("coach_lead_requests")
          .select("id", { count: "exact", head: true })
          .eq("coach_id", user.id)
          .eq("status", "pending");

        if (pendingLeads && pendingLeads > 0) {
          alerts.push({
            text: `${pendingLeads} pending lead${pendingLeads > 1 ? "s" : ""}`,
            route: "/coach",
          });
        }
      }

      // --- User alerts (everyone) ---
      const { count: incomingRequests } = await supabase
        .from("coach_requests")
        .select("id", { count: "exact", head: true })
        .eq("client_id", user.id)
        .eq("status", "pending");

      if (incomingRequests && incomingRequests > 0) {
        alerts.push({
          text: `${incomingRequests} incoming coach request${incomingRequests > 1 ? "s" : ""}`,
          route: "/profile",
        });
      }

      // Upcoming appointments within 24 hours
      const now = new Date().toISOString();
      const in24h = new Date(Date.now() + ONE_DAY_MS).toISOString();
      const { count: upcomingAppts } = await supabase
        .from("appointments")
        .select("id", { count: "exact", head: true })
        .or(`user_id.eq.${user.id},coach_id.eq.${user.id}`)
        .eq("status", "booked")
        .gte("starts_at_utc", now)
        .lte("starts_at_utc", in24h);

      if (upcomingAppts && upcomingAppts > 0) {
        alerts.push({
          text: `${upcomingAppts} appointment${upcomingAppts > 1 ? "s" : ""} in the next 24h`,
          route: isCoach ? "/coach" : "/my-coach",
        });
      }

      // Coach cert rejected
      if (isCoach) {
        const { count: rejectedCerts } = await supabase
          .from("coach_certifications")
          .select("id", { count: "exact", head: true })
          .eq("coach_id", user.id)
          .eq("status", "rejected");

        if (rejectedCerts && rejectedCerts > 0) {
          alerts.push({
            text: `${rejectedCerts} certification${rejectedCerts > 1 ? "s" : ""} rejected — resubmit required`,
            route: "/coach",
          });
        }
      }
    } catch {
      // Silently ignore — non-critical feature
      return;
    }

    if (alerts.length === 0) {
      // Don't mark as shown when no alerts found — allows re-check
      // on next page load in case new items appear during the session
      return;
    }

    // Pick the most important route (first alert) for the action button
    const primaryRoute = alerts[0].route;

    toast({
      title: "⚡ Needs your attention",
      description: alerts.map((a) => a.text).join(" · "),
      duration: 8000,
      action: createElement(
        ToastAction,
        {
          altText: "View",
          onClick: () => navigateRef.current(primaryRoute),
        },
        "View"
      ) as unknown as ReactElement<typeof ToastAction>,
    });

    markShown();
  }
}
